// Copy colours from… (an object's colours onto others: an armed pick, the next
// click takes the source). docs/claude/selection.md.
import { takesFinish } from '../../shared/finishes';
import {
  MULTI_KINDS,
  type MultiKind,
  selectedItems,
} from '../selection';
import {
  type RoomFinishes,
  type RoomWoods,
  type Vec2,
} from '../../shared/types';
import { takesWood } from '../../shared/woods';
import {
  hasCeiling,
  ownCeilingColor,
  setCeilingColor,
} from '../ceiling';
import {
  invalidate,
  pushHistory,
  renderProperties,
  state,
  vp,
} from './core';
import { tr } from '../i18n';
import {
  hitTest,
  hitTestNonRoom,
  objectAt,
  regenerateSauna,
} from '../main';
import { armPick } from './armedPick';
import type { ObjItem } from '../drawingObjects';
import { toPlan } from './view3d';

/** Copy an object's colours (fill, and a room's wall/bench colours and woods) onto another. */
// A normalized colour set: a surface fill, a line/outline stroke, their
// opacities, and the instance-only wall/bench/text extras. Reading and writing
// map each object kind's own colour fields into/out of this, so a copy is exact
// between same-kind objects and a sensible best-effort across kinds.
interface ColorSet { fill?: string; fillOpacity?: number; stroke?: string; strokeOpacity?: number; wall?: string; bench?: string; text?: string; ceiling?: string | null; woods?: RoomWoods | null; finishes?: RoomFinishes | null; }

/** What Copy colours takes from and gives to: an object of the multi kinds
 *  (selection.ts `MultiKind`, so a new one does not compile until
 *  `readColorSet` / `applyColorSet` say how). */
export type ColorTarget = Extract<ObjItem, { kind: MultiKind }>;

function readColorSet(t: ColorTarget): ColorSet {
  switch (t.kind) {
    case 'instance': return {
      fill: t.obj.color, wall: t.obj.wallColor, bench: t.obj.benchColor, ceiling: hasCeiling(t.obj) ? ownCeilingColor(t.obj) : undefined,
      woods: takesWood(t.obj) ? (t.obj.woods ? structuredClone(t.obj.woods) : null) : undefined,
      finishes: takesFinish(t.obj) ? (t.obj.finishes ? structuredClone(t.obj.finishes) : null) : undefined,
    };
    case 'polygon': return { fill: t.obj.fill, fillOpacity: t.obj.fillOpacity, stroke: t.obj.stroke, strokeOpacity: t.obj.strokeOpacity };
    case 'wall': return { fill: t.obj.fill, fillOpacity: t.obj.fillOpacity, stroke: t.obj.stroke, strokeOpacity: t.obj.strokeOpacity };
    case 'text': return { fill: t.obj.fill, fillOpacity: t.obj.fillOpacity, stroke: t.obj.color, strokeOpacity: t.obj.strokeOpacity, text: t.obj.textColor };
    case 'arc': return { stroke: t.obj.stroke };
    case 'leader': return { stroke: t.obj.color };
    case 'cloud': return { stroke: t.obj.color };
  }
}

function applyColorSet(t: ColorTarget, c: ColorSet): void {
  const fill = c.fill ?? c.wall ?? c.stroke;    // best "surface" colour from the source
  const stroke = c.stroke ?? c.fill ?? c.wall;  // best "line/outline" colour
  switch (t.kind) {
    case 'instance': {
      const i = t.obj;
      if (fill) i.color = fill;
      i.wallColor = c.wall ?? fill ?? i.wallColor;
      i.benchColor = c.bench ?? c.wall ?? fill ?? i.benchColor;
      if (hasCeiling(i) && c.ceiling !== undefined) setCeilingColor(i, c.ceiling); // null: the ceiling follows the walls
      if (takesWood(i) && c.woods !== undefined) { if (c.woods) i.woods = structuredClone(c.woods); else delete i.woods; } // null: no wood
      if (takesFinish(i) && c.finishes !== undefined) { if (c.finishes) i.finishes = structuredClone(c.finishes); else delete i.finishes; } // null: the defaults
      if (i.autoBenches) regenerateSauna(i); // bench fills are baked into geometry
      break;
    }
    case 'polygon': case 'wall': {
      const p = t.obj;
      if (fill) p.fill = fill;
      if (stroke) p.stroke = stroke;
      if (c.fillOpacity != null) p.fillOpacity = c.fillOpacity;
      if (c.strokeOpacity != null) p.strokeOpacity = c.strokeOpacity;
      break;
    }
    case 'text': {
      const x = t.obj;
      if (fill) x.fill = fill;
      x.textColor = c.text ?? stroke ?? x.textColor;
      if (c.stroke) x.color = c.stroke;
      if (c.fillOpacity != null) x.fillOpacity = c.fillOpacity;
      if (c.strokeOpacity != null) x.strokeOpacity = c.strokeOpacity;
      break;
    }
    case 'arc': if (stroke) t.obj.stroke = stroke; break;
    case 'leader': if (stroke) t.obj.color = stroke; break;
    case 'cloud': if (stroke) t.obj.color = stroke; break;
  }
}

/** Copy the source object's colours onto one or more destinations (any kinds). */
function copyColorsBetween(dests: ColorTarget[], src: ColorTarget): void {
  const targets = dests.filter((d) => d.obj !== src.obj);
  if (!targets.length) return;
  pushHistory();
  const set = readColorSet(src);
  for (const d of targets) applyColorSet(d, set);
  renderProperties();
  invalidate();
}

/** The object under the cursor, of any selectable kind (same precedence as
 *  toggleAnySelection), for picking a colour source. */
function pickColorTarget(world: Vec2): ColorTarget | null {
  const instId = hitTestNonRoom(world) ?? hitTest(world);
  if (instId) { const o = state.drawing.instances.find((i) => i.id === instId); if (o) return { kind: 'instance', obj: o }; }
  const poly = objectAt('polygon', world); if (poly) return { kind: 'polygon', obj: poly };
  const wall = objectAt('wall', world); if (wall) return { kind: 'wall', obj: wall };
  const text = objectAt('text', world); if (text) return { kind: 'text', obj: text };
  const arc = objectAt('arc', world); if (arc) return { kind: 'arc', obj: arc };
  const leader = objectAt('leader', world); if (leader) return { kind: 'leader', obj: leader };
  const cloud = objectAt('cloud', world); if (cloud) return { kind: 'cloud', obj: cloud };
  return null;
}

/** Every currently-selected object as a colour target (used to wire the "Copy
 *  colours from…" button — colours are copied onto all of them at once). */
export function selectedColorTargets(): ColorTarget[] {
  return selectedItems(state, state.drawing, MULTI_KINDS) as ColorTarget[];
}

/** Begin a "copy colours from…" pick: the next canvas click copies the clicked
 *  object's colours onto `dests`. */
export function startCopyColors(dests: ColorTarget[]): void {
  toPlan();
  armPick({
    kind: 'copy-colours',
    cursor: 'copy',
    prompt: () => tr('Click an object to copy its colours onto the selection (Esc to cancel)'),
    // Empty space (or the same object) picks nothing: the pick is over either way.
    click: (_e, sp) => { const src = pickColorTarget(vp.screenToWorld(sp)); if (src) copyColorsBetween(dests, src); },
    cancel: () => {},
  });
}
